import { useCallback, useMemo } from 'react'
import { cn } from '../../utils'

interface AutocompleteDropdownProps {
  fieldName: string
  testId: string
  suggestions: string[]
  activeSuggestionIndex: number
  onSelectSuggestion: (suggestion: string) => void
  setActiveSuggestionIndex: (index: number) => void
  fieldValue?: string | null
}

/**
 * Autocomplete dropdown component.
 */
export function AutocompleteDropdown({
  fieldName,
  testId,
  suggestions,
  activeSuggestionIndex,
  onSelectSuggestion,
  setActiveSuggestionIndex,
  fieldValue
}: Readonly<AutocompleteDropdownProps>) {
  const sortedSuggestions = useMemo(() => {
    // if the fieldValue is set, move it to the top of suggestions
    if (fieldValue && suggestions.includes(fieldValue)) {
      const index = suggestions.indexOf(fieldValue, 0) // get index of fieldValue in suggestions
      const res = suggestions.toSpliced(index, 1) // remove fieldValue from suggestions
      res.unshift(fieldValue) // insert fieldValue to the front of suggestions
      return res
    }
    return suggestions
  }, [fieldValue, suggestions])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, suggestion: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelectSuggestion(suggestion)
      }
    },
    [onSelectSuggestion]
  )

  const handleMouseEnter = useCallback(
    (index: number) => {
      setActiveSuggestionIndex(index)
    },
    [setActiveSuggestionIndex]
  )

  // Only show dropdown if there are suggestions
  if (sortedSuggestions.length === 0) {
    return null
  }

  return (
    <div
      id={`${fieldName}-suggestions`}
      data-testid={`${testId}-suggestions`}
      aria-labelledby={`${fieldName}-label`}
      className="absolute z-10 min-w-full w-auto max-w-[150%] max-h-[297px] overflow-y-auto bg-cool-gray-100 border border-cool-gray-200 divide-y divide-cool-gray-200 rounded-sm shadow-sm"
      role="listbox"
    >
      {sortedSuggestions.map((suggestion, index) => (
        <div
          key={suggestion}
          id={`${fieldName}-suggestion-${index}`}
          data-testid={`${testId}-suggestion-${index}`}
          className={cn('cursor-pointer px-1 py-0.5', {
            'bg-cool-gray-200':
              index === activeSuggestionIndex || suggestion === fieldValue
          })}
          role="option"
          tabIndex={0}
          aria-selected={index === activeSuggestionIndex}
          onClick={() => onSelectSuggestion(suggestion)}
          onMouseDown={(e) => {
            // Prevent blur on input when clicking suggestion
            e.preventDefault()
          }}
          onKeyDown={(e) => handleKeyDown(e, suggestion)}
          onMouseEnter={() => handleMouseEnter(index)}
        >
          {suggestion}
        </div>
      ))}
    </div>
  )
}
